import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import {
  ArrowUpRight, ArrowDownLeft, CheckCircle2, Circle, Play, KeyRound, Loader2,
  Phone, Hash, CalendarClock,
} from "lucide-react";

export default function OpsToday() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/vendor/ops/today")
      .then(({ data }) => setData(data))
      .catch(() => setData({ departures: [], returns: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  }

  const departures = data?.departures || [];
  const returns = data?.returns || [];
  const dateLabel = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="ops-today">
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-violet-600">Opérations du jour</div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 capitalize">{dateLabel}</h1>
        </div>

        {/* Départs */}
        <SectionTitle icon={ArrowUpRight} color="text-violet-600" label="Départs" count={departures.length} testid="ops-departures-count" />
        {departures.length === 0 ? (
          <EmptyCard text="Aucun départ prévu aujourd'hui." testid="ops-departures-empty" />
        ) : (
          <div className="space-y-3 mb-8">
            {departures.map((b) => <DepartureCard key={b.id} b={b} navigate={navigate} />)}
          </div>
        )}

        {/* Retours */}
        <SectionTitle icon={ArrowDownLeft} color="text-sky-600" label="Retours" count={returns.length} testid="ops-returns-count" />
        {returns.length === 0 ? (
          <EmptyCard text="Aucun retour prévu aujourd'hui." testid="ops-returns-empty" />
        ) : (
          <div className="space-y-3">
            {returns.map((b) => <ReturnCard key={b.id} b={b} navigate={navigate} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, color, label, count, testid }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className={`h-4 w-4 ${color}`} />
      <h2 className="text-sm font-bold text-slate-900">{label}</h2>
      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-bold" data-testid={testid}>{count}</span>
    </div>
  );
}

function EmptyCard({ text, testid }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center text-sm text-slate-400 mb-8" data-testid={testid}>
      <CalendarClock className="h-5 w-5 mx-auto mb-2 text-slate-300" />
      {text}
    </div>
  );
}

function ProgressChip({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />} {label}
    </span>
  );
}

function DepartureCard({ b, navigate }) {
  const started = b.info_verified || b.edl.created;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-violet-200 transition-colors" data-testid={`ops-departure-${b.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {b.start_time && <span className="px-2 py-0.5 rounded-md bg-violet-600 text-white text-[11px] font-bold">{b.start_time}</span>}
            <span className="text-sm font-bold text-slate-900 truncate">{b.client_name}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="truncate">{b.vehicle_name}</span>
            {b.license_plate && <span className="inline-flex items-center gap-1 font-mono text-slate-700"><Hash className="h-3 w-3" />{b.license_plate}</span>}
            {b.client_phone && <a href={`tel:${b.client_phone}`} className="inline-flex items-center gap-1 text-violet-600 font-semibold"><Phone className="h-3 w-3" />{b.client_phone}</a>}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-3">
        <ProgressChip ok={b.edl.signed} label="EDL" />
        <ProgressChip ok={b.contract_signed} label="Contrat" />
        <ProgressChip ok={b.keys_handed} label="Clés" />
      </div>
      <div className="mt-3">
        {b.keys_handed ? (
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600" data-testid={`ops-departure-done-${b.id}`}>
            <KeyRound className="h-3.5 w-3.5" /> Clés remises — location en cours
          </div>
        ) : (
          <button
            onClick={() => navigate(`/vendor/depart/${b.id}`)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-10 px-5 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700"
            data-testid={`ops-start-departure-${b.id}`}
          >
            <Play className="h-3.5 w-3.5" /> {started ? "Reprendre le départ" : "Démarrer le départ"}
          </button>
        )}
      </div>
    </div>
  );
}

function ReturnCard({ b, navigate }) {
  const canStart = b.status === "in_progress" && !b.keys_returned;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-200 transition-colors" data-testid={`ops-return-${b.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {b.end_time && <span className="px-2 py-0.5 rounded-md bg-sky-600 text-white text-[11px] font-bold">{b.end_time}</span>}
            <span className="text-sm font-bold text-slate-900 truncate">{b.client_name}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500 truncate">
            {b.vehicle_name}{b.license_plate ? ` · ${b.license_plate}` : ""}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-3">
        <ProgressChip ok={b.edl.signed || !!b.keys_returned} label="EDL retour" />
        <ProgressChip ok={!!b.keys_returned} label="Clôture" />
      </div>
      <div className="mt-3">
        {b.keys_returned || b.status === "completed" ? (
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600" data-testid={`ops-return-done-${b.id}`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Retour terminé — location clôturée
          </div>
        ) : canStart ? (
          <button
            onClick={() => navigate(`/vendor/retour/${b.id}`)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-10 px-5 rounded-xl bg-sky-600 text-white text-sm font-bold hover:bg-sky-700"
            data-testid={`ops-start-return-${b.id}`}
          >
            <Play className="h-3.5 w-3.5" /> {b.return_started ? "Reprendre le retour" : "Démarrer le retour"}
          </button>
        ) : (
          <div className="text-xs font-semibold text-slate-400" data-testid={`ops-return-waiting-${b.id}`}>
            En attente du départ (la location n'est pas encore « En cours »)
            <button onClick={() => navigate(`/vendor/pilotage?tab=bookings&booking=${b.id}`)} className="ml-2 text-sky-600 font-bold hover:underline">Ouvrir</button>
          </div>
        )}
      </div>
    </div>
  );
}
